import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, AlertCircle, Globe } from 'lucide-react';
import { translations } from '../i18n/translations';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    business_context: '',
    language: 'fr',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Get translations based on selected language
  const t = translations[formData.language] || translations.fr;

  const formatError = (detail) => {
    if (!detail) return t.common.error;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
    }
    if (detail?.msg) return detail.msg;
    return String(detail);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await register(formData.name, formData.email, formData.password, formData.business_context, formData.language);
      navigate('/onboarding');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      data-testid="register-page"
      className="min-h-screen bg-atlasBg flex items-center justify-center p-4 relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1771448234067-5de85bece13b?w=1920)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-atlasBg via-atlasBg/90 to-atlasBg/70" />
      </div>

      {/* Glow effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-atlasPurple/10 rounded-full blur-3xl" />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="atlas-brand text-5xl text-white mb-2 tracking-[0.2em]">ATLAS</h1>
          <p className="text-atlasTextSecondary">{(translations[formData.language] || translations.fr).splash.tagline}</p>
        </div>

        {/* Register Card */}
        <div className="glass-card p-8 glow-purple">
          <h2 className="text-2xl font-semibold text-white mb-6 text-center">{t.auth.createAccount}</h2>

          {error && (
            <div className="mb-4 p-3 bg-atlasRed/10 border border-atlasRed/30 rounded-lg flex items-center gap-2 text-atlasRed">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Language Selector - First thing */}
            <div>
              <Label className="text-atlasTextSecondary flex items-center gap-2">
                <Globe className="w-4 h-4" />
                {t.auth.language}
              </Label>
              <Select 
                value={formData.language} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, language: value }))}
              >
                <SelectTrigger data-testid="register-language-select" className="input-atlas mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">🇫🇷 Français</SelectItem>
                  <SelectItem value="en">🇬🇧 English</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="name" className="text-atlasTextSecondary">{t.auth.name}</Label>
              <Input
                id="name"
                name="name"
                type="text"
                data-testid="register-name-input"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                className="input-atlas mt-1"
                required
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-atlasTextSecondary">{t.auth.email}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                data-testid="register-email-input"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@company.com"
                className="input-atlas mt-1"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-atlasTextSecondary">{t.auth.password}</Label>
              <Input
                id="password"
                name="password"
                type="password"
                data-testid="register-password-input"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="input-atlas mt-1"
                required
                minLength={6}
              />
            </div>

            <div>
              <Label htmlFor="business_context" className="text-atlasTextSecondary">{t.auth.businessContext}</Label>
              <Textarea
                id="business_context"
                name="business_context"
                data-testid="register-context-input"
                value={formData.business_context}
                onChange={handleChange}
                placeholder={t.auth.businessContextPlaceholder}
                className="input-atlas mt-1 min-h-[80px]"
              />
            </div>

            <Button
              type="submit"
              data-testid="register-submit-btn"
              disabled={isLoading}
              className="w-full btn-primary h-12 text-base"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t.common.loading}
                </>
              ) : (
                t.auth.createAccount
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <span className="text-atlasTextSecondary">{t.auth.hasAccount} </span>
            <Link to="/login" className="text-atlasCyan hover:underline" data-testid="login-link">
              {t.auth.signInLink}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
